import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
	InstanciaRelatorio,
	PeriodosLabel,
} from '@home/submodulos/dados-meteorologicos/interfaces/tipos-relatorios';

import { Validators } from '@angular/forms';
import { HighStockComponent } from '@componentes/high-stock/high-stock.component';
import { optionsChartObservadoDesvioClimatologia } from '@modulos/home/submodulos/dados-meteorologicos/componentes/relatorios/grafico-desvios-e-totais-periodo/chartOptions';
import { format } from 'date-fns-tz';
import * as Highcharts from 'highcharts/highstock';
import { Subject, takeUntil } from 'rxjs';

import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import {
	alinharDado,
	baixarEAbrirPDF,
	criarImagemBase64FromChart,
	enumAsSelectOptions,
	isNuloOuUndefined,
	numberToBrNumberTables,
	verificaSePDFEAdicionaNaTabela,
} from '@utils';
import * as pdfseira from '@utils/pdf-seira';
import { ToastrService } from 'ngx-toastr';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import {
	corrigeDuplicacaoNome,
	gerarFilenameGrafico,
	obterLabelPeriodo,
} from '../../../utils';
import { DateTimeUtils } from '@utils/datetime-util';
import {
	AgrupamentoEspecial,
	RelatorioDesvio,
} from '@home/submodulos/dados-meteorologicos/interfaces';
import { RelatoriosService } from '@home/submodulos/dados-meteorologicos/services';
import { DocumentExporter } from '@utils/document-exporter';
import * as ExcelTable from 'mr-excel';
import { Select } from '@layout/interfaces/select';
import { NumberUtils } from '@utils/number-utils';

@Component({
	selector: 'seira-desvios-e-totais-periodo',
	templateUrl: './grafico-desvios-e-totais-periodo.component.html',
	styleUrls: ['./grafico-desvios-e-totais-periodo.component.scss'],
})
export class GraficoDesviosETotaisPeriodoComponent
	implements OnInit, OnDestroy, InstanciaRelatorio
{
	@ViewChild(HighStockComponent, { static: false })
	highStockComponent: HighStockComponent;
	inputs = inject(INPUTS_RELATORIOS);
	chart: Highcharts.Chart;
	loading = false;
	gerou = false;
	_destroyed = new Subject();
	get isPorcentagem(): boolean {
		return (
			this.inputs.form.get(FormularioRelatorio.TIPO)?.value ===
			'DESVIO_POR_PERIODO_PORCENTAGEM'
		);
	}

	get tipoDesvio(): string {
		return this.isPorcentagem ? 'porcentagem' : 'milimetro';
	}

	resultado = false;
	agrupamento: Select[] | any = [];
	chartOptions: Highcharts.Options = optionsChartObservadoDesvioClimatologia(
		this.inputs.form.get(FormularioRelatorio.TIPO)?.value ===
			'DESVIO_POR_PERIODO_PORCENTAGEM'
	);

	descricaoRelatorioMilimetro =
		'Define-se como desvios e totais por período (mm), a diferença entre a pluviometria registrada e a sua climatologia para um mesmo período, em um determinado local ou região.';
	descricaoRelatorioPorcentagem =
		'Define-se como desvios e totais por período (%), a variação da pluviometria em relação à climatologia, expresso em porcentagem, indicando se o acumulado de chuva foi superior ou inferior à climatologia.';

	get descricaoRelatorio(): string {
		return this.tipoDesvio === 'milimetro'
			? this.descricaoRelatorioMilimetro
			: this.descricaoRelatorioPorcentagem;
	}

	botoesDeExportacao: GroupButton[] = [
		{
			label: '.png',
			size: 'small',
			icon: 'ph-image',
			onClick: () => this.exportarPNG(),
		},
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () => this.exportarPDF(),
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];
	periodoTitulo = '';
	preciptacoesAgrupadas: any[];
	anos: string[];
	chartAux: any;
	tituloAgrupamento: string;

	constructor(
		private toastr: ToastrService,
		private readonly relatorioService: RelatoriosService
	) {
		this.agrupamento = enumAsSelectOptions(AgrupamentoEspecial);
	}

	ngOnInit() {
		this.setValidatorsByAgrupamento(
			this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value
		);
		this.setValidators();
	}

	setChart(chart: Highcharts.Chart) {
		this.chart = chart;
	}

	ngOnDestroy() {
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.ESTACAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MUNICIPIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MICRORREGIAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MESORREGIAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.BACIA)?.clearValidators();
		this.inputs.form
			.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
			?.clearValidators();
		this._destroyed.next(undefined);
	}

	setValidatorsByAgrupamento(agrupamentoValue: string) {
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.MUNICIPIO);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.ESTACAO);
		this.handleRemoveValidatorsFromFormControl(
			FormularioRelatorio.MICRORREGIAO
		);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.BACIA);

		switch (agrupamentoValue) {
			case 'MUNICIPIO_POSTO':
				this.inputs.form
					?.get(FormularioRelatorio.ESTACAO)
					?.setValidators(Validators.required);
				break;
			case 'MICRORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MICRORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'MESORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MESORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'MUNICIPIO':
				this.inputs.form
					?.get(FormularioRelatorio.MUNICIPIO)
					?.setValidators(Validators.required);
				break;
			case 'BACIA_SUBBACIA':
				this.inputs.form
					?.get(FormularioRelatorio.BACIA)
					?.setValidators(Validators.required);
				break;
			case 'REGIAO_PLUVIOMETRICA':
				this.inputs.form
					?.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
					?.setValidators(Validators.required);
				break;
		}
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: value => {
					this.setValidatorsByAgrupamento(value);
				},
			});

		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
	}

	handleRemoveValidatorsFromFormControl(data: FormularioRelatorio) {
		this.inputs.form.get(data)?.clearValidators();
		this.inputs.form.get(data)?.updateValueAndValidity({ emitEvent: false });
	}

	gerarRelatorio() {
		this.loading = true;
		this.inputs.setLoading(true);
		const periodoAgrupamento = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value as PeriodosLabel;
		const dataInicio = this.inputs.form.get(
			FormularioRelatorio.DATA_INICIO
		)?.value;
		const dataFim = this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.value;

		const diaInicio =
			periodoAgrupamento !== 'anual' && periodoAgrupamento !== 'anual_parcial'
				? dataInicio
				: new Date(`${new Date(dataInicio).getFullYear()}-01-01T00:00:00`);
		const diaFim =
			periodoAgrupamento !== 'anual' && periodoAgrupamento !== 'anual_parcial'
				? dataFim
				: new Date(`${new Date(dataFim).getFullYear()}-12-31T00:00:00`);

		const agrupamento = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;
		let id = null;

		if (
			!diaInicio ||
			!diaFim ||
			!agrupamento ||
			!periodoAgrupamento ||
			this.inputs.form.invalid
		) {
			return;
		}

		const periodoFormatado = DateTimeUtils.formatarDataPeriodo(
			diaInicio,
			diaFim,
			periodoAgrupamento
		);
		const labelPeriodo = obterLabelPeriodo(periodoAgrupamento);
		this.periodoTitulo = `${labelPeriodo} (${periodoFormatado})`;

		switch (agrupamento) {
			case 'MUNICIPIO_POSTO':
				id = this.inputs.form.get('estacao')?.value;
				break;
			case 'MICRORREGIAO':
				id = this.inputs.form.get('microrregiao')?.value;
				break;
			case 'MESORREGIAO':
				id = this.inputs.form.get('mesorregiao')?.value;
				break;
			case 'REGIAO_PLUVIOMETRICA':
				id = this.inputs.form.get('regiao')?.value;
				break;
			case 'BACIA_SUBBACIA':
				id = this.inputs.form.get('bacia')?.value;
				break;
			case 'MUNICIPIO':
				id = this.inputs.form.get('municipio')?.value;
		}

		this.loading = true;
		this.inputs.setLoading(true);

		this.relatorioService
			.buscarRelatorioDesvio({
				agrupamento,
				idEntidade: id === '0' ? null : id,
				dataFim: new Date(diaFim),
				dataInicio: new Date(diaInicio),
				periodo: periodoAgrupamento.toUpperCase(),
				incluirDetalhes: false,
			})
			.subscribe({
				next: preciptacoesAgrupadas => {
					this.relatorioService.atualizaVisibilidadeBotoesExportacao(
						preciptacoesAgrupadas
					);
					this.preciptacoesAgrupadas = preciptacoesAgrupadas;
					this.configurarChart(preciptacoesAgrupadas, periodoAgrupamento);
					this.chartOptions = {
						...this.chartOptions,
						exporting: {
							enabled: false,
							buttons: {
								contextButton: {
									enabled: false,
								},
							},
							filename: gerarFilenameGrafico(
								`grafico_pluviometria_observada_desvio_${
									this.tipoDesvio === 'porcentagem' ? '%' : 'mm'
								}_${this.inputs.form
									.get(FormularioRelatorio.AGRUPAMENTO)
									?.value.toLocaleLowerCase()}_${periodoAgrupamento.toLocaleLowerCase()}`
							),
						},
					};
					this.resultado = !!preciptacoesAgrupadas[0];
				},
				error: () => {
					this.inputs.setLoading(false);
					this.resultado = false;
					this.loading = false;
					this.gerou = false;
					this.toastr.error(
						'Ocorreu um erro ao buscar os dados de desvios no servidor',
						'Erro ao buscar desvios'
					);
				},
				complete: () => {
					this.gerou = true;
					this.loading = false;
					this.inputs.setLoading(false);
				},
			});
	}

	getTipoPrecipitacao(
		tipoAgrupamento: any,
		precipitacao: any,
		isXLSX: boolean,
		tipoDesvio?: string,
		dados?: any[],
		desvio?: {
			data: string;
			chuva: number;
			desvioMilimetro?: number;
			desvioPorcentagem?: number;
			climatologia: number;
		}
	) {
		if (!isXLSX) {
			if (tipoAgrupamento === 'MUNICIPIO_POSTO') {
				return corrigeDuplicacaoNome(precipitacao.municipio);
			} else if (tipoAgrupamento === 'MUNICIPIO') {
				return precipitacao.municipio;
			} else if (tipoAgrupamento === 'MICRORREGIAO') {
				return precipitacao.microrregiao;
			} else if (tipoAgrupamento === 'MESORREGIAO') {
				return precipitacao.mesorregiao;
			} else if (tipoAgrupamento === 'BACIA_SUBBACIA') {
				return precipitacao.bacia;
			} else if (tipoAgrupamento === 'REGIAO_PLUVIOMETRICA') {
				return precipitacao.regiaoPluviometrica;
			}
		} else {
			const formatarNumeroXLSX = (valor: string | number | null | undefined) =>
				isNuloOuUndefined(valor) ? '-' : numberToBrNumberTables(valor, 1);

			const formatarDadosBase = (desvio: {
				data: string;
				chuva: string | number | null | undefined;
				climatologia: string | number | null | undefined;
			}) => ({
				data: DateTimeUtils.formatarData(
					DateTimeUtils.formatToZonedDateTime(desvio.data),
					'MM/yyyy'
				),
				chuva: formatarNumeroXLSX(desvio.chuva),
				climatologia: formatarNumeroXLSX(desvio.climatologia),
			});

			const obterDadosAgrupamento = (
				groupType: string | number,
				precipitacao: {
					municipio: string;
					microrregiao: any;
					mesorregiao: any;
					bacia: any;
					regiaoPluviometrica: any;
					subBacia: any;
				}
			) => {
				const mapping = {
					MUNICIPIO_POSTO: {
						municipio: precipitacao.municipio
							? corrigeDuplicacaoNome(precipitacao.municipio)
							: '',
					},
					MUNICIPIO: { municipio: precipitacao.municipio },
					MICRORREGIAO: { microrregiao: precipitacao.microrregiao },
					MESORREGIAO: { mesorregiao: precipitacao.mesorregiao },
					BACIA_SUBBACIA: { bacia: precipitacao.bacia },
					REGIAO_PLUVIOMETRICA: {
						regiaoPluviometrica: precipitacao.regiaoPluviometrica,
					},
				};
				return mapping[groupType as keyof typeof mapping];
			};

			const addDados = (
				groupType: any,
				precipitacao: any,
				desvio: {
					data: string;
					chuva: number;
					desvioMilimetro?: number;
					desvioPorcentagem?: number;
					climatologia: number;
				},
				tipoDesvio: string | undefined
			) => {
				const baseData = formatarDadosBase(desvio);
				const agrupamento = obterDadosAgrupamento(groupType, precipitacao);
				const desvioKey =
					tipoDesvio === 'milimetro' ? 'desvioMilimetro' : 'desvioPorcentagem';

				dados?.push({
					...baseData,
					...agrupamento,
					[desvioKey]: formatarNumeroXLSX(desvio[desvioKey]),
				});
			};

			addDados(tipoAgrupamento, precipitacao, desvio as any, tipoDesvio);
		}
	}

	getDadosTabelaParaExportacao(isPdf: boolean) {
		const tableData: any[][] = [];
		const tipoPeriodo = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA);
		const tipoAgrupamento = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;

		const colunas = [
			{
				text: AgrupamentoEspecial[
					tipoAgrupamento as keyof typeof AgrupamentoEspecial
				],
				fillColor: '#DCDCDC',
				alignment: 'center',
			},
			{
				text: 'Data',
				fillColor: '#DCDCDC',
				alignment: 'center',
			},
			{ text: 'Observado (mm)', fillColor: '#DCDCDC', alignment: 'center' },
			{ text: 'Climatologia (mm)', fillColor: '#DCDCDC', alignment: 'center' },
			{
				text: this.tipoDesvio === 'milimetro' ? 'Desvio (mm)' : 'Desvio (%)',
				fillColor: '#DCDCDC',
				alignment: 'center',
			},
		];

		verificaSePDFEAdicionaNaTabela(tableData, colunas, isPdf);

		let rowData: any[] = [];

		if (tipoPeriodo?.value === 'mensal') {
			this.preciptacoesAgrupadas.forEach(precipitacao => {
				if (this.tipoDesvio === 'milimetro') {
					precipitacao.desvios.forEach(
						(desvio: {
							data: string;
							chuva: number;
							desvioMilimetro: number;
							climatologia: number;
						}) => {
							rowData = [
								tipoAgrupamento === 'MUNICIPIO_POSTO'
									? this.getTipoPrecipitacao(
											tipoAgrupamento,
											precipitacao,
											false
										)
									: alinharDado(
											this.getTipoPrecipitacao(
												tipoAgrupamento,
												precipitacao,
												false
											),
											isPdf
										),
								alinharDado(
									DateTimeUtils.formatarData(
										DateTimeUtils.formatToZonedDateTime(desvio.data),
										'MM/yyyy'
									),
									isPdf
								),
								alinharDado(
									isNuloOuUndefined(desvio.chuva)
										? '-'
										: NumberUtils.getIgnorandoCasasDecimais(desvio.chuva),
									isPdf
								),
								alinharDado(
									isNuloOuUndefined(desvio.climatologia)
										? '-'
										: NumberUtils.getIgnorandoCasasDecimais(
												desvio.climatologia
											),
									isPdf
								),
								alinharDado(
									isNuloOuUndefined(desvio.desvioMilimetro)
										? '-'
										: NumberUtils.getIgnorandoCasasDecimais(
												desvio.desvioMilimetro
											),
									isPdf
								),
							];

							tableData.push(rowData);
						}
					);
				} else {
					precipitacao.desvios.forEach(
						(desvio: {
							data: string;
							chuva: number;
							desvioPorcentagem: number;
							climatologia: number;
						}) => {
							rowData = [
								tipoAgrupamento === 'MUNICIPIO_POSTO'
									? this.getTipoPrecipitacao(
											tipoAgrupamento,
											precipitacao,
											false
										)
									: alinharDado(
											this.getTipoPrecipitacao(
												tipoAgrupamento,
												precipitacao,
												false
											),
											isPdf
										),
								alinharDado(
									DateTimeUtils.formatarData(
										DateTimeUtils.formatToZonedDateTime(desvio.data),
										'MM/yyyy'
									),
									isPdf
								),
								alinharDado(
									isNuloOuUndefined(desvio.chuva)
										? '-'
										: NumberUtils.getIgnorandoCasasDecimais(desvio.chuva),
									isPdf
								),
								alinharDado(
									isNuloOuUndefined(desvio.climatologia)
										? '-'
										: NumberUtils.getIgnorandoCasasDecimais(
												desvio.climatologia
											),
									isPdf
								),
								alinharDado(
									isNuloOuUndefined(desvio.desvioPorcentagem)
										? '-'
										: NumberUtils.getIgnorandoCasasDecimais(
												desvio.desvioPorcentagem
											),
									isPdf
								),
							];

							tableData.push(rowData);
						}
					);
				}
			});
		} else {
			let rowData: any[] = [];

			this.anos.map(ano => {
				const dadosDoAno = this.preciptacoesAgrupadas[0].desvios.filter(
					(item: any) => item.data.startsWith(ano)
				);

				const chuvaAnual = dadosDoAno.reduce(
					(total: any, item: any) => total + item.chuva,
					0
				);

				const climatologiaAnual = dadosDoAno.reduce(
					(total: any, item: any) => total + item.climatologia,
					0
				);

				const desvioMilimetroAnual = dadosDoAno.reduce(
					(total: any, item: any) => total + item.desvioMilimetro,
					0
				);

				const desvioPorcentagemAnual =
					climatologiaAnual !== 0
						? (desvioMilimetroAnual / climatologiaAnual) * 100
						: 0;

				this.preciptacoesAgrupadas.forEach(precipitacao => {
					if (this.tipoDesvio === 'milimetro') {
						rowData = [
							tipoAgrupamento === 'MUNICIPIO_POSTO'
								? this.getTipoPrecipitacao(tipoAgrupamento, precipitacao, false)
								: alinharDado(
										this.getTipoPrecipitacao(
											tipoAgrupamento,
											precipitacao,
											false
										),
										isPdf
									),
							alinharDado(ano, isPdf),
							alinharDado(numberToBrNumberTables(chuvaAnual, 1), isPdf),
							alinharDado(
								numberToBrNumberTables(Number(climatologiaAnual), 1),
								isPdf
							),
							alinharDado(
								numberToBrNumberTables(desvioMilimetroAnual, 1),
								isPdf
							),
						];

						tableData.push(rowData);
					} else {
						rowData = [
							tipoAgrupamento === 'MUNICIPIO_POSTO'
								? this.getTipoPrecipitacao(tipoAgrupamento, precipitacao, false)
								: alinharDado(
										this.getTipoPrecipitacao(
											tipoAgrupamento,
											precipitacao,
											false
										),
										isPdf
									),
							alinharDado(ano, isPdf),
							alinharDado(numberToBrNumberTables(chuvaAnual, 1), isPdf),
							alinharDado(
								numberToBrNumberTables(Number(climatologiaAnual), 1),
								isPdf
							),
							alinharDado(
								numberToBrNumberTables(desvioPorcentagemAnual, 1),
								isPdf
							),
						];

						tableData.push(rowData);
					}
				});
			});
		}

		return tableData;
	}

	retornarDadosXLSX() {
		const dados: any[] = [];
		const tipoPeriodo = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA);
		const tipoAgrupamento = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;

		if (tipoPeriodo?.value === 'mensal') {
			this.preciptacoesAgrupadas.forEach(precipitacao => {
				if (this.tipoDesvio === 'milimetro') {
					precipitacao?.desvios.forEach(
						(desvio: {
							data: string;
							chuva: number;
							desvioMilimetro: number;
							desvioPorcentagem: number;
							climatologia: number;
						}) => {
							this.getTipoPrecipitacao(
								tipoAgrupamento,
								precipitacao,
								true,
								this.tipoDesvio,
								dados,
								desvio
							);
						}
					);
				} else {
					precipitacao.desvios.forEach(
						(desvio: {
							data: string;
							chuva: number;
							desvioPorcentagem: number;
							climatologia: number;
						}) => {
							this.getTipoPrecipitacao(
								tipoAgrupamento,
								precipitacao,
								true,
								this.tipoDesvio,
								dados,
								desvio
							);
						}
					);
				}
			});
		} else {
			this.anos.map(ano => {
				const dadosDoAno = this.preciptacoesAgrupadas[0].desvios.filter(
					(item: any) => item.data.startsWith(ano)
				);
				const chuvaAnual = dadosDoAno.reduce(
					(total: any, item: any) => total + item.chuva,
					0
				);

				const climatologiaAnual = dadosDoAno.reduce(
					(total: any, item: any) => total + item.climatologia,
					0
				);

				const desvioMilimetroAnual = dadosDoAno.reduce(
					(total: any, item: any) => total + item.desvioMilimetro,
					0
				);

				const desvioPorcentagemAnual =
					climatologiaAnual !== 0
						? (desvioMilimetroAnual / climatologiaAnual) * 100
						: 0;

				this.preciptacoesAgrupadas.forEach(precipitacao => {
					const registro: any = {};
					if (
						tipoAgrupamento === 'MUNICIPIO_POSTO' ||
						tipoAgrupamento === 'MUNICIPIO'
					) {
						registro.municipio = precipitacao.municipio;
					} else if (tipoAgrupamento === 'MICRORREGIAO') {
						registro.microrregiao = precipitacao.microrregiao;
					} else if (tipoAgrupamento === 'MESORREGIAO') {
						registro.mesorregiao = precipitacao.mesorregiao;
					} else if (tipoAgrupamento === 'BACIA_SUBBACIA') {
						registro.bacia = precipitacao.bacia;
					} else if (tipoAgrupamento === 'REGIAO_PLUVIOMETRICA') {
						registro.regiaoPluviometrica = precipitacao.regiaoPluviometrica;
					} else {
						registro.subbacia = precipitacao.subBacia;
					}
					registro.data = ano;
					registro.chuva = this.formatarNumeroXLSX(chuvaAnual);
					registro.climatologia = this.formatarNumeroXLSX(climatologiaAnual);

					if (this.tipoDesvio === 'milimetro') {
						registro.desvioMilimetro =
							this.formatarNumeroXLSX(desvioMilimetroAnual);
					} else {
						registro.desvioPorcentagem = this.formatarNumeroXLSX(
							desvioPorcentagemAnual
						);
					}
					dados.push({
						...registro,
					});
				});
			});
		}

		return dados;
	}

	extrairDatasPrecipitacao(
		preciptacaoAgrupada: RelatorioDesvio,
		periodoAgrupamento: PeriodosLabel
	) {
		return [
			...new Set(
				preciptacaoAgrupada.desvios
					.filter((el: any) => el.data)
					.map((el: any) => {
						const [ano, mes, dia] = el.data!.split('-');
						return new Date(+ano, (+mes || 1) - 1, +dia || 1);
					})
					.sort((a: any, b: any) => +a - +b)
					.map((data: any) => {
						const formato = 'MM/yyyy';
						if (
							periodoAgrupamento === 'anual' ||
							periodoAgrupamento === 'anual_parcial'
						) {
							return data.getFullYear();
						}
						return format(data, formato, {
							timeZone: 'America/Sao_Paulo',
						});
					})
			),
		];
	}

	configurarChart(
		preciptacoesAgrupadas: any[],
		periodoAgrupamento: PeriodosLabel
	) {
		const sufixoTipo = this.tipoDesvio === 'milimetro' ? 'mm' : '%';
		const categorias = ['Observado', 'Climatologia', `Desvio ${sufixoTipo}`];

		this.chartOptions = optionsChartObservadoDesvioClimatologia(
			this.isPorcentagem
		);

		this.chartOptions.title = {
			text: this.getTituloRelatorio(),
			align: 'center',
			style: {
				fontSize: '18px',
			},
		};

		const dataSeries: string[] =
			preciptacoesAgrupadas.length === 0
				? []
				: this.extrairDatasPrecipitacao(
						preciptacoesAgrupadas[0],
						periodoAgrupamento
					);

		this.chartOptions.series = categorias.map(serie => {
			let resultado: any[] = [];
			const y = serie !== `Desvio ${sufixoTipo}` ? 0 : 1;
			const sufixo = serie === 'Desvio %' ? '%' : 'mm';
			const tipo = serie == `Desvio ${sufixoTipo}` ? 'spline' : 'column';

			if (dataSeries.length === 0) {
				resultado = [];
			} else if (periodoAgrupamento === 'mensal') {
				let i = 0;
				if (serie === 'Observado') {
					resultado = preciptacoesAgrupadas[0].desvios.map((dados: any) => {
						const retorno = [dataSeries[i], dados?.chuva];
						i += 1;
						return retorno;
					});
				} else if (serie === 'Climatologia') {
					resultado = preciptacoesAgrupadas[0].desvios.map((dados: any) => {
						const retorno = [dataSeries[i], dados?.climatologia];
						i += 1;
						return retorno;
					});
				} else {
					resultado = preciptacoesAgrupadas[0].desvios.map((dados: any) => {
						const retorno = [
							dataSeries[i],
							this.tipoDesvio === 'milimetro'
								? dados?.desvioMilimetro
								: dados?.desvioPorcentagem,
						];
						i += 1;
						return retorno;
					});
				}
			} else {
				this.anos = dataSeries;
				const dadosAgrupados = dataSeries.map(ano => {
					const dadosDoAno = preciptacoesAgrupadas[0].desvios.filter(
						(item: any) => item.data.startsWith(ano)
					);

					const chuvaAnual = dadosDoAno.reduce(
						(total: any, item: any) => total + item.chuva,
						0
					);

					const climatologiaAnual = dadosDoAno.reduce(
						(total: any, item: any) => total + item.climatologia,
						0
					);

					const desvioMilimetroAnual = dadosDoAno.reduce(
						(total: any, item: any) => total + item.desvioMilimetro,
						0
					);

					const desvioPorcentagemAnual =
						climatologiaAnual !== 0
							? (desvioMilimetroAnual / climatologiaAnual) * 100
							: 0;

					return {
						ano,
						chuvaAnual,
						climatologiaAnual,
						desvioMilimetroAnual,
						desvioPorcentagemAnual,
					};
				});

				if (serie === 'Observado') {
					resultado = dadosAgrupados.map(dados => [
						dados.ano,
						dados.chuvaAnual,
					]);
				} else if (serie === 'Climatologia') {
					resultado = dadosAgrupados.map(dados => [
						dados.ano,
						dados.climatologiaAnual,
					]);
				} else {
					resultado = dadosAgrupados.map(dados => [
						dados.ano,
						this.tipoDesvio === 'milimetro'
							? dados.desvioMilimetroAnual
							: dados.desvioPorcentagemAnual,
					]);
				}
			}

			return {
				name: serie,
				type: tipo,
				tooltip: {
					valueDecimals: 1,
					valueSuffix: sufixo,
				},
				yAxis: y,
				marker: {
					enabled: y,
					radius: 5,
				},
				zIndex: y * 10,
				data: resultado,
			} as Highcharts.SeriesColumnOptions;
		});

		this.chartOptions.xAxis = {
			categories: dataSeries,
		};
	}

	private getTituloRelatorio() {
		return `Desvios e totais por períodos (${
			this.tipoDesvio === 'porcentagem' ? '%' : 'mm'
		}) - ${this.periodoTitulo}`;
	}

	private formatarNumeroXLSX(valor: string | number | null | undefined) {
		return isNuloOuUndefined(valor) ? '-' : numberToBrNumberTables(valor, 1);
	}

	async exportarPDF() {
		if (this.chart.xAxis && this.chart.yAxis) {
			this.chartAux = this.chart;
		}

		const chartsConverted: TDocumentDefinitions['content'] = [];

		if (!this.chart || !this.resultado) {
			this.toastr.error(
				'Não é possível gerar um pdf pois nenhum gráfico foi gerado.'
			);
			return;
		}

		chartsConverted.push({
			image: await this.gerarImagemExportacaoComLogos(2200, true),
			fontSize: 10,
			alignment: 'center',
			height: 400,
			width: 750,
		});

		const documentDefinition: any =
			await pdfseira.documentDefinitions('landscape');

		documentDefinition.pageOrientation = 'landscape';

		documentDefinition.footer = (currentPage: number, pageCount: number) => {
			return {
				margin: [40, 0, 40, 20],
				text: `Pagina ${currentPage} de ${pageCount}`,
				alignment: 'center',
				fontSize: 8,
			};
		};

		const tituloPdf = this.getTituloRelatorio();

		const content = [
			{
				text: '',
				margin: [0, 30, 5, 15],
			},
			...chartsConverted,
			{
				table: {
					widths: ['20%', '20%', '20%', '20%', '20%'],
					body: this.getDadosTabelaParaExportacao(true),
					layout: {
						noWrap: false,
						fontSize: 5,
					},
				},

				marginTop: 10,
			},
		];

		documentDefinition.content.push(content);

		baixarEAbrirPDF(tituloPdf, documentDefinition);
	}

	async exportarPNG() {
		if (this.chart.xAxis && this.chart.yAxis) {
			this.chartAux = this.chart;
		}

		if (!this.chart || !this.resultado) {
			this.toastr.error(
				'NÃ£o Ã© possÃ­vel exportar PNG pois nenhum grÃ¡fico foi gerado.'
			);
			return;
		}

		try {
			const dataUrl = await this.gerarImagemExportacaoComLogos();
			const nomeArquivo = gerarFilenameGrafico(
				`grafico_pluviometria_observada_desvio_${
					this.tipoDesvio === 'porcentagem' ? '%' : 'mm'
				}_${this.inputs.form
					.get(FormularioRelatorio.AGRUPAMENTO)
					?.value.toLocaleLowerCase()}_${this.inputs.form
					.get(FormularioRelatorio.PERIODO_BUSCA)
					?.value.toLocaleLowerCase()}`
			);

			const link = document.createElement('a');
			link.href = dataUrl;
			link.download = `${nomeArquivo}.png`;

			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (e) {
			console.error('[DESVIO/TOTAL] Falha ao exportar PNG:', e);
			this.toastr.error('Erro ao exportar o PNG do grÃ¡fico.');
		}
	}

	exportarCSV() {
		const tableData = this.getDadosTabelaParaExportacao(false);

		DocumentExporter.gerarCSV(tableData, this.getTituloRelatorio());
	}

	exportarTXT() {
		const tableData = this.getDadosTabelaParaExportacao(false);

		let txtData = '';
		if (tableData.length > 0) {
			const colunas = tableData[0];

			tableData.slice(1).forEach(dadoRelatorio => {
				colunas.forEach((coluna: string, index: number) => {
					const valor = dadoRelatorio[index];
					txtData += `${coluna}: ${valor ?? '-'}\n`;
				});

				txtData += '\n';
			});
		}

		DocumentExporter.gerarTXT(txtData, this.getTituloRelatorio());
	}

	exportarXLSX() {
		const tipoAgrupamento = this.inputs.form.get('agrupamento')?.value;
		const nomeArquivo = this.getTituloRelatorio();
		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};
		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const headers = [
			tipoAgrupamento === 'MUNICIPIO_POSTO'
				? {
						label: 'municipio',
						text: 'Município/Posto',
						size: 48,
					}
				: tipoAgrupamento === 'MUNICIPIO'
					? {
							label: 'municipio',
							text: 'Município',
							size: 48,
						}
					: tipoAgrupamento === 'MICRORREGIAO'
						? {
								label: 'microrregiao',
								text: 'Microrregião',
								size: 48,
							}
						: tipoAgrupamento === 'MESORREGIAO'
							? {
									label: 'mesorregiao',
									text: 'Mesorregião',
									size: 48,
								}
							: tipoAgrupamento === 'BACIA_SUBBACIA'
								? {
										label: 'bacia',
										text: 'Bacia/Sub-bacia',
										size: 48,
									}
								: tipoAgrupamento === 'REGIAO_PLUVIOMETRICA'
									? {
											label: 'regiaoPluviometrica',
											text: 'Região pluviométrica',
											size: 48,
										}
									: {
											label: 'subbacia',
											text: 'Sub-bacia',
											size: 48,
										},
		];

		headers.push(
			{
				label: 'data',
				text: 'Data',
				size: 24,
			},
			{ label: 'chuva', text: 'Observado (mm)', size: 24 },
			{ label: 'climatologia', text: 'Climatologia (mm)', size: 24 },
			{
				label:
					this.tipoDesvio === 'milimetro'
						? 'desvioMilimetro'
						: 'desvioPorcentagem',
				text: this.tipoDesvio === 'milimetro' ? 'Desvio (mm)' : 'Desvio (%)',
				size: 24,
			}
		);
		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleCenter,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number,
						fromHeader: boolean,
						styleKeys: string[]
					) {
						if (data && data.label) {
							return 'headerStyleCenter';
						} else {
							if (colIndex === 0 && tipoAgrupamento === 'MUNICIPIO_POSTO') {
								return 'rowStyle';
							} else {
								return 'rowStyleCenter';
							}
						}
					},
					headers: headers,
					data: this.retornarDadosXLSX(),
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: nomeArquivo,
						styleId: 'title',
					},
				},
			],
			fileName: nomeArquivo,
		};

		ExcelTable.generateExcel(dataExcel);
	}

	private carregarImagem(src: string): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = err => reject(err);
			img.src = src;
		});
	}

	private async gerarImagemExportacaoComLogos(
		width = 1200,
		ocultarTitulo = false
	): Promise<string> {
		const tituloAtual = this.chartAux?.title?.textStr ?? '';

		if (ocultarTitulo && this.chartAux?.setTitle) {
			this.chartAux.setTitle({ text: '' });
		}

		try {
			const chartBase64 = await criarImagemBase64FromChart(
				this.chartAux,
				width
			);
			const baseImg = await this.carregarImagem(chartBase64);
			const footerHeight = Math.round(baseImg.height * 0.14);
			const canvas = document.createElement('canvas');
			canvas.width = baseImg.width;
			canvas.height = baseImg.height + footerHeight;

			const ctx = canvas.getContext('2d');
			if (!ctx) {
				throw new Error('Canvas context nao disponivel.');
			}

			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(0, 0, canvas.width, canvas.height);
			ctx.drawImage(baseImg, 0, 0, baseImg.width, baseImg.height);

			const footerY = baseImg.height;
			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(0, footerY, canvas.width, footerHeight);

			try {
				const margem = canvas.width * 0.02;
				const larguraLogoAesa = canvas.width * 0.08;
				const larguraLogoGoverno = canvas.width * 0.1;

				const logoAesa = await this.carregarImagem(
					'assets/images/logos/logoAesa.svg'
				);
				const alturaLogoAesa =
					(larguraLogoAesa / logoAesa.width) * logoAesa.height;
				const xLogoAesa = margem;
				const yLogoAesa = footerY + (footerHeight - alturaLogoAesa) / 2;

				ctx.drawImage(
					logoAesa,
					xLogoAesa,
					yLogoAesa,
					larguraLogoAesa,
					alturaLogoAesa
				);

				const logoGoverno = await this.carregarImagem(
					'assets/images/logos/governo_pb.png'
				);
				const alturaLogoGoverno =
					(larguraLogoGoverno / logoGoverno.width) * logoGoverno.height;
				const xLogoGoverno = canvas.width - larguraLogoGoverno - margem;
				const yLogoGoverno = footerY + (footerHeight - alturaLogoGoverno) / 2;

				ctx.drawImage(
					logoGoverno,
					xLogoGoverno,
					yLogoGoverno,
					larguraLogoGoverno,
					alturaLogoGoverno
				);
			} catch (e) {
				console.error('[DESVIO/TOTAL EXPORT] Erro ao desenhar as logos:', e);
			}

			return canvas.toDataURL('image/png', 1);
		} finally {
			if (ocultarTitulo && this.chartAux?.setTitle) {
				this.chartAux.setTitle({ text: tituloAtual });
			}
		}
	}
}
